import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

// Helper function to adjust color lightness for lighter shade
function adjustColorLightness(hex, percent) {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Convert to HSL
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
            case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
            case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
            case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
            default: h = 0;
        }
    }
    
    // Adjust lightness
    l = Math.min(1, Math.max(0, l + percent / 100));
    
    // Convert back to RGB
    let r2, g2, b2;
    if (s === 0) {
        r2 = g2 = b2 = l;
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r2 = hue2rgb(p, q, h + 1/3);
        g2 = hue2rgb(p, q, h);
        b2 = hue2rgb(p, q, h - 1/3);
    }
    
    // Convert to hex
    const toHex = (val) => {
        const hex = Math.round(val * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
}

export default function FeatureFlagToggle() {
    const { user, refreshFeatureFlags } = useAuth();
    const [featureFlags, setFeatureFlags] = useState({});
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);
    const [viewMode, setViewMode] = useState('user'); // 'user' or 'global'
    const [tempThemeColor, setTempThemeColor] = useState(null); // Local state for color picker
    const [colorChanged, setColorChanged] = useState(false); // Track if color has changed

    const fetchFeatureFlags = useCallback(async () => {
        try {
            setLoading(true);
            let url;

            if (viewMode === 'user' && user) {
                // Get user-specific flags with overrides
                url = `${API_URL}/api/feature-flags/user/${user.id}`;
            } else {
                // Get global flags (admin view)
                url = `${API_URL}/api/feature-flags`;
            }

            const response = await fetch(url);
            if (response.ok) {
                const flags = await response.json();
                setFeatureFlags(flags);
            } else {
                console.error('Failed to fetch feature flags');
            }
        } catch (error) {
            console.error('Error fetching feature flags:', error);
        } finally {
            setLoading(false);
        }
    }, [user, viewMode]);

    useEffect(() => {
        fetchFeatureFlags();
    }, [fetchFeatureFlags]);

    // Initialize tempThemeColor when feature flags load
    useEffect(() => {
        if (featureFlags.themeColor?.value) {
            setTempThemeColor(featureFlags.themeColor.value);
        }
    }, [featureFlags.themeColor?.value]);

    // Initialize theme from localStorage on mount (for immediate display before feature flags load)
    useEffect(() => {
        const savedDarkTheme = localStorage.getItem('darkTheme') === 'true';
        if (savedDarkTheme) {
            document.body.classList.add('dark-theme');
        }
    }, []);

    const toggleFeatureFlag = async (flagName, currentValue) => {
        try {
            setUpdating(flagName);
            let url, method;
            
            if (viewMode === 'user' && user) {
                // Toggle user-specific override
                url = `${API_URL}/api/feature-flags/user/${user.id}/${flagName}`;
                method = 'PUT';
            } else {
                // Toggle global flag (admin)
                url = `${API_URL}/api/feature-flags/${flagName}`;
                method = 'PUT';
            }
            
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    enabled: !currentValue,
                    // Preserve the value for themeColor when toggling
                    ...(flagName === 'themeColor' && featureFlags.themeColor?.value && { value: featureFlags.themeColor.value })
                }),
            });

            if (response.ok) {
                const updatedFlag = await response.json();
                setFeatureFlags(prev => ({
                    ...prev,
                    ...updatedFlag
                }));
                
                // If toggling darkTheme or themeColor in user view, refresh globally
                if ((flagName === 'darkTheme' || flagName === 'themeColor') && viewMode === 'user') {
                    // Refresh feature flags to apply changes globally
                    if (refreshFeatureFlags) {
                        await refreshFeatureFlags();
                    }
                }
            } else {
                console.error('Failed to update feature flag');
            }
        } catch (error) {
            console.error('Error updating feature flag:', error);
        } finally {
            setUpdating(null);
        }
    };

    const removeUserOverride = async (flagName) => {
        if (!user) return;
        
        try {
            setUpdating(flagName);
            const response = await fetch(
                `${API_URL}/api/feature-flags/user/${user.id}/${flagName}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                const updatedFlag = await response.json();
                setFeatureFlags(prev => ({
                    ...prev,
                    ...updatedFlag
                }));
                
                // If resetting darkTheme or themeColor, refresh globally
                if (flagName === 'darkTheme' || flagName === 'themeColor') {
                    if (refreshFeatureFlags) {
                        await refreshFeatureFlags();
                    }
                }
            }
        } catch (error) {
            console.error('Error removing user override:', error);
        } finally {
            setUpdating(null);
        }
    };

    const updateThemeColorValue = async (colorValue) => {
        if (!user || viewMode !== 'user') return;
        
        try {
            setUpdating('themeColor');
            const url = `${API_URL}/api/feature-flags/user/${user.id}/themeColor`;
            
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    enabled: featureFlags.themeColor?.enabled ?? true, 
                    value: colorValue 
                }),
            });

            if (response.ok) {
                const updatedFlag = await response.json();
                setFeatureFlags(prev => ({
                    ...prev,
                    ...updatedFlag
                }));
                
                // Refresh feature flags to apply changes globally
                if (refreshFeatureFlags) {
                    await refreshFeatureFlags();
                }
            }
        } catch (error) {
            console.error('Error updating theme color:', error);
        } finally {
            setUpdating(null);
        }
    };

    // Handle local color change (no API call)
    const handleColorChange = (colorValue) => {
        setTempThemeColor(colorValue);
        setColorChanged(true);
        
        // Apply preview immediately (no database save)
        document.documentElement.style.setProperty('--theme-primary', colorValue);
        const lighterColor = adjustColorLightness(colorValue, 20);
        document.documentElement.style.setProperty('--theme-primary-light', lighterColor);
    };

    // Save the color to the database
    const saveThemeColor = async () => {
        if (!tempThemeColor || !colorChanged) return;
        
        await updateThemeColorValue(tempThemeColor);
        setColorChanged(false);
    };

    // Cancel color changes and revert to saved value
    const cancelColorChange = () => {
        const savedColor = featureFlags.themeColor?.value || '#0d6efd';
        setTempThemeColor(savedColor);
        setColorChanged(false);
        
        // Revert to saved color
        document.documentElement.style.setProperty('--theme-primary', savedColor);
        const lighterColor = adjustColorLightness(savedColor, 20);
        document.documentElement.style.setProperty('--theme-primary-light', lighterColor);
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Feature Flags</h5>
                </div>
                <div className="card-body">
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card mt-3">
            <div className="card-header">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                    <div>
                        <h5 className="mb-0">🏴 Feature Flags</h5>
                        <small className="text-muted">
                            {viewMode === 'user' 
                                ? `Personal settings for ${user?.username || 'current user'}`
                                : 'Global defaults (affects all users without custom settings)'}
                        </small>
                    </div>
                    {user && (
                        <div className="btn-group btn-group-sm" role="group">
                            <button
                                type="button"
                                className={`btn ${viewMode === 'user' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setViewMode('user')}
                            >
                                <i className="bi bi-person me-1"></i>
                                My Settings
                            </button>
                            <button
                                type="button"
                                className={`btn ${viewMode === 'global' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setViewMode('global')}
                            >
                                <i className="bi bi-globe me-1"></i>
                                Global
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="card-body">
                {Object.keys(featureFlags).length === 0 ? (
                    <p className="text-muted">No feature flags available</p>
                ) : (
                    <div className="row g-3">
                        {Object.entries(featureFlags).map(([flagName, flag]) => (
                            <div key={flagName} className="col-12">
                                <div className="d-flex justify-content-between align-items-center p-3 border rounded">
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <strong className="text-dark">{flagName}</strong>
                                            <span className={`badge ${flag.enabled ? 'bg-success' : 'bg-secondary'}`}>
                                                {flag.enabled ? 'ON' : 'OFF'}
                                            </span>
                                            {viewMode === 'user' && flag.isUserOverride && (
                                                <span className="badge bg-info">
                                                    <i className="bi bi-person-fill me-1"></i>
                                                    Custom
                                                </span>
                                            )}
                                        </div>
                                        {flag.description && (
                                            <small className="text-muted d-block mt-1">
                                                {flag.description}
                                            </small>
                                        )}
                                        {flagName === 'themeColor' && flag.enabled && viewMode === 'user' && (
                                            <div className="mt-2">
                                                <label htmlFor="themeColorPicker" className="form-label small mb-1">
                                                    <i className="bi bi-palette me-1"></i>
                                                    Choose your primary color:
                                                </label>
                                                <div className="d-flex gap-2 align-items-center">
                                                    <input
                                                        type="color"
                                                        id="themeColorPicker"
                                                        className="form-control form-control-color"
                                                        value={tempThemeColor !== null ? tempThemeColor : (flag.value || '#0d6efd')}
                                                        onChange={(e) => handleColorChange(e.target.value)}
                                                        disabled={updating === 'themeColor'}
                                                        title="Choose primary color"
                                                    />
                                                    {colorChanged && (
                                                        <>
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={saveThemeColor}
                                                                disabled={updating === 'themeColor'}
                                                                title="Save color"
                                                            >
                                                                <i className="bi bi-check-lg"></i> Save
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={cancelColorChange}
                                                                disabled={updating === 'themeColor'}
                                                                title="Cancel changes"
                                                            >
                                                                <i className="bi bi-x-lg"></i>
                                                            </button>
                                                        </>
                                                    )}
                                                    {updating === 'themeColor' && (
                                                        <span className="spinner-border spinner-border-sm" role="status">
                                                            <span className="visually-hidden">Saving...</span>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex gap-2 align-items-center">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`switch-${flagName}`}
                                                checked={flag.enabled}
                                                onChange={() => toggleFeatureFlag(flagName, flag.enabled)}
                                                disabled={updating === flagName}
                                            />
                                            <label className="form-check-label" htmlFor={`switch-${flagName}`}>
                                                {updating === flagName && (
                                                    <span className="spinner-border spinner-border-sm ms-2" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </span>
                                                )}
                                            </label>
                                        </div>
                                        {viewMode === 'user' && flag.isUserOverride && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => removeUserOverride(flagName)}
                                                disabled={updating === flagName}
                                                title="Reset to global default"
                                            >
                                                <i className="bi bi-arrow-counterclockwise"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="mt-3 d-flex justify-content-end">
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={fetchFeatureFlags}
                        disabled={loading}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </button>
                </div>
            </div>
        </div>
    );
}